/**
 * AR Aging tab: the arithmetic of Zay's AR aging dashboard, on our snapshot.
 *
 * Wave 1 of the rebuild on the client's own tools (AJ, 2026-09-23). Zay built
 * an AR aging dashboard for Delta over the Navusoft aging report; this module
 * is its logic, rule for rule, run on the newest `public.ar_current` snapshot
 * instead of a CSV dropped into a browser. Every number the tab shows comes
 * out of a function here, so the node:test suite pins the arithmetic without
 * a browser (scripts/ar-aging.test.mjs).
 *
 * WHAT IS THE CLIENT'S AND WHAT IS OURS
 *
 *   - The bucket rules, the priority tag, the critical test, the 31 to 90 day
 *     warning test, the top 40 collections list and every total are Zay's, as
 *     his tool computes them. Where his tool is internally inconsistent (the
 *     segment panels' "past due" starts at 31 days, the All Accounts "Has Past
 *     Due" filter starts at 1) both are kept as he wrote them, because the
 *     point of this tab is that it reads the same as the tool Delta already
 *     trusts.
 *   - The residential rule is NOT here. It is a customer config value
 *     (`ar_segments.residential` in customers/<slug>/config.yaml), stamped
 *     onto each widget by resolve-manifest.mjs (applyArAgingConfig). Delta's
 *     rule is a bill group containing "Quarterly"; Best Disposal bills monthly
 *     only and declares none, so its tab has no commercial / residential
 *     split at all rather than an empty residential side (MER-819).
 *
 * SUM OVER SUM (MER-715). The only ratio on the tab is a bucket's share of
 * total AR, and it is the bucket's dollar sum over the Total column's dollar
 * sum. Nothing averages a per-account ratio.
 *
 * ONE SNAPSHOT. `ar_current` keeps every export as a dated snapshot and a sum
 * across them reads several times the real receivable (MER-729). The fetch
 * reads the newest import_date only, and latestSnapshot() below drops any
 * older row a caller hands in, so no path here can add two snapshots.
 *
 * Plain .mjs so node:test imports it directly; types in arAging.d.mts.
 */

export const AR_AGING_TABLE = "ar_current";

/**
 * The six aging buckets, youngest first, as Zay's tool orders them. `column`
 * is the ar_current column (the Navusoft export's own name, snake cased).
 */
export const AR_AGING_BUCKETS = Object.freeze([
  { key: "cu", column: "current_amount", label: "Current" },
  { key: "d30", column: "amount_1_to_30", label: "1-30 Days" },
  { key: "d60", column: "amount_31_to_60", label: "31-60 Days" },
  { key: "d90", column: "amount_61_to_90", label: "61-90 Days" },
  { key: "d120", column: "amount_91_to_120", label: "91-120 Days" },
  { key: "ov", column: "over_120_amount", label: "Over 120" },
]);

export const AR_AGING_BUCKET_KEYS = Object.freeze(AR_AGING_BUCKETS.map((b) => b.key));

/** Every column the tab reads. Nothing else is fetched. */
export const AR_AGING_COLUMNS = Object.freeze([
  "import_date",
  "account_id",
  "account_name",
  "account_status_text",
  "division_name",
  "billgroup_name",
  "average_days_to_pay",
  ...AR_AGING_BUCKETS.map((b) => b.column),
  "total",
]);

/** The three statuses Zay's tool counts. Anything else is reported as other. */
export const AR_AGING_STATUSES = Object.freeze(["Active", "Credit Hold", "Inactive"]);

/** How many accounts the 91+ collections list keeps (Zay: slice(0, 40)). */
export const RISK_LIST_SIZE = 40;

/** The All Accounts age filters, Zay's labels and rules. */
export const AGE_FILTERS = Object.freeze([
  { key: "any", label: "Any balance" },
  { key: "past_due", label: "Has past due (1-30 and older)" },
  { key: "d60", label: "60+ days" },
  { key: "d90", label: "90+ days" },
  { key: "current", label: "Current only" },
]);

/** Zay's pn(): a money string or number to a number, anything else 0. */
export function toNumber(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const n = Number.parseFloat(String(value).replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

/** Round to the cent, the way Zay's tool rounds every total it shows. */
export function cents(value) {
  return Math.round(value * 100) / 100;
}

/**
 * The residential rule as the resolver stamped it, or null when the customer
 * declared none. Accepts the stamped shape `{label, bill_groups,
 * bill_group_contains}`; anything else is treated as no rule, never as a
 * default one.
 */
export function normalizeSegmentRule(rule) {
  if (!rule || typeof rule !== "object") return null;
  const exact = Array.isArray(rule.bill_groups)
    ? rule.bill_groups.filter((g) => typeof g === "string" && g !== "")
    : [];
  const contains = Array.isArray(rule.bill_group_contains)
    ? rule.bill_group_contains.filter((g) => typeof g === "string" && g !== "")
    : [];
  if (exact.length === 0 && contains.length === 0) return null;
  const label =
    typeof rule.label === "string" && rule.label.trim() ? rule.label.trim() : "Residential";
  return { label, bill_groups: exact, bill_group_contains: contains };
}

/**
 * "residential" when the bill group is on the customer's list or contains one
 * of the customer's substrings (case sensitive, as Zay's `includes` is),
 * otherwise "commercial". A blank bill group is commercial, as it is in Zay's
 * tool (`''.includes('Quarterly')` is false). Null when there is no rule.
 */
export function segmentOf(billGroup, rule) {
  if (!rule) return null;
  const bg = typeof billGroup === "string" ? billGroup : "";
  if (rule.bill_groups.includes(bg)) return "residential";
  for (const needle of rule.bill_group_contains) {
    if (bg.includes(needle)) return "residential";
  }
  return "commercial";
}

/** Rows of the newest import_date only (defensive; the fetch already does this). */
export function latestSnapshot(rows) {
  const list = Array.isArray(rows) ? rows : [];
  let latest = "";
  for (const r of list) {
    const d = r && r.import_date ? String(r.import_date).slice(0, 10) : "";
    if (d > latest) latest = d;
  }
  if (!latest) return { date: null, rows: [] };
  return {
    date: latest,
    rows: list.filter((r) => r && String(r.import_date ?? "").slice(0, 10) === latest),
  };
}

/** One ar_current row to the account shape every view works on. */
export function toAccount(row, rule) {
  const r = row ?? {};
  const statusRaw = typeof r.account_status_text === "string" ? r.account_status_text.trim() : "";
  const account = {
    id: r.account_id === null || r.account_id === undefined ? null : String(r.account_id),
    name: typeof r.account_name === "string" ? r.account_name.trim() : "",
    // Zay's tool reads a blank status as Active.
    status: statusRaw || "Active",
    division: typeof r.division_name === "string" ? r.division_name : null,
    billGroup: typeof r.billgroup_name === "string" ? r.billgroup_name : "",
    segment: null,
    total: toNumber(r.total),
    days: toNumber(r.average_days_to_pay),
  };
  for (const b of AR_AGING_BUCKETS) account[b.key] = toNumber(r[b.column]);
  account.segment = segmentOf(account.billGroup, rule);
  return account;
}

/** Normalise a division name or id for comparison ("Central Coast" = "central_coast"). */
export function divisionKey(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Keep the accounts in the selected divisions. An empty selection keeps
 * everything. Matching is case and separator blind because the tenants store
 * division names in different casings (MER-504).
 */
export function filterByDivision(accounts, divisions) {
  const wanted = (Array.isArray(divisions) ? divisions : []).map(divisionKey).filter(Boolean);
  if (wanted.length === 0) return accounts;
  const set = new Set(wanted);
  return accounts.filter((a) => set.has(divisionKey(a.division)));
}

/** 91-120 plus Over 120: the dollars Zay's tool calls at risk. */
export function atRisk(account) {
  return (account.d120 || 0) + (account.ov || 0);
}

/** Zay's critical test: any 91+ balance, or average days to pay over 90. */
export function isCritical(account) {
  return (account.d120 || 0) > 0 || (account.ov || 0) > 0 || (account.days || 0) > 90;
}

/** Zay's priTag(). */
export function priority(account) {
  const ov = account.ov || 0;
  const d120 = account.d120 || 0;
  const d90 = account.d90 || 0;
  const dy = account.days || 0;
  if (ov > 0 || dy > 90) return "Critical";
  if (d120 > 0 || dy > 60) return "High";
  if (d90 > 0 || dy > 30) return "Medium";
  return "Low";
}

function sumBy(accounts, pick) {
  let s = 0;
  for (const a of accounts) s += pick(a) || 0;
  return s;
}

function bucketTotals(accounts) {
  const t = {};
  for (const k of AR_AGING_BUCKET_KEYS) t[k] = cents(sumBy(accounts, (a) => a[k]));
  t.total = cents(sumBy(accounts, (a) => a.total));
  return t;
}

/**
 * Zay's mkGrp(): one segment's bucket totals, account count and "past due".
 * Past due here is 31 days and older (31-60 + 61-90 + 91-120 + Over 120), as
 * his segment panels compute it; 1-30 is deliberately not in it.
 */
export function segmentSummary(accounts, segment) {
  const group = accounts.filter((a) => a.segment === segment);
  const t = bucketTotals(group);
  return {
    segment,
    totals: t,
    count: group.length,
    pastDue: cents(t.d60 + t.d90 + t.d120 + t.ov),
  };
}

/**
 * Accounts with a positive balance in one bucket, largest first, each carrying
 * `bucketValue`. Zay's DB.bkt[key].
 */
export function bucketAccounts(accounts, key) {
  return accounts
    .filter((a) => (a[key] || 0) > 0)
    .map((a) => ({ ...a, bucketValue: a[key] }))
    .sort((x, y) => y.bucketValue - x.bucketValue);
}

/**
 * Everything the Overview half of the tab shows, from one account list.
 * `segments` is null when the customer declared no residential rule.
 */
export function summarize(accounts, rule) {
  const list = Array.isArray(accounts) ? accounts : [];
  const totals = bucketTotals(list);
  const bucketCounts = {};
  for (const k of AR_AGING_BUCKET_KEYS)
    bucketCounts[k] = list.filter((a) => (a[k] || 0) > 0).length;
  const status = { active: 0, hold: 0, inactive: 0, other: 0 };
  for (const a of list) {
    if (a.status === "Active") status.active += 1;
    else if (a.status === "Credit Hold") status.hold += 1;
    else if (a.status === "Inactive") status.inactive += 1;
    else status.other += 1;
  }
  return {
    accountCount: list.length,
    totals,
    bucketCounts,
    status,
    segments: rule
      ? {
          commercial: segmentSummary(list, "commercial"),
          residential: segmentSummary(list, "residential"),
        }
      : null,
  };
}

/** A bucket's share of total AR, sum over sum. Null when total AR is zero. */
export function shareOfTotal(amount, total) {
  if (!total) return null;
  return amount / total;
}

/** Zay's DB.risk: the top 40 accounts by 91+ balance. */
export function riskList(accounts, size = RISK_LIST_SIZE) {
  return accounts
    .filter((a) => atRisk(a) > 0)
    .map((a) => ({ ...a, bucketValue: atRisk(a) }))
    .sort((x, y) => y.bucketValue - x.bucketValue)
    .slice(0, size);
}

/** Zay's warning table: a 31-60 or 61-90 balance and nothing 91 or older. */
export function warningList(accounts) {
  return accounts.filter((a) => {
    const has = (a.d60 || 0) > 0 || (a.d90 || 0) > 0;
    const crit = (a.d120 || 0) > 0 || (a.ov || 0) > 0;
    return has && !crit;
  });
}

/**
 * Zay's renderCrit(): the critical rows and the four cards above them. The
 * cards are computed from the rows passed in, so a type or status filter on
 * the table moves the cards with it, exactly as his tool does.
 */
export function criticalSummary(accounts) {
  const rows = accounts.filter(isCritical).map((a) => ({ ...a, bucketValue: atRisk(a) }));
  const totalRisk = cents(sumBy(rows, (a) => a.bucketValue));
  const commercial = rows.filter((a) => a.segment === "commercial");
  const residential = rows.filter((a) => a.segment === "residential");
  const commercialRisk = cents(sumBy(commercial, (a) => a.bucketValue));
  return {
    rows,
    count: rows.length,
    totalRisk,
    totalOwed: cents(sumBy(rows, (a) => a.total)),
    holdCount: rows.filter((a) => a.status === "Credit Hold").length,
    commercialRisk,
    // Zay: totalRisk - commRisk.
    residentialRisk: cents(totalRisk - commercialRisk),
    commercialCount: commercial.length,
    residentialCount: residential.length,
  };
}

/** Zay's buildBucketPage() cards for one bucket and the filtered rows under them. */
export function bucketPageSummary(allInBucket, filteredRows) {
  const pick = (rows) => ({
    bucket: cents(sumBy(rows, (a) => a.bucketValue)),
    owed: cents(sumBy(rows, (a) => a.total)),
    count: rows.length,
  });
  return {
    all: pick(allInBucket),
    commercial: pick(allInBucket.filter((a) => a.segment === "commercial")),
    residential: pick(allInBucket.filter((a) => a.segment === "residential")),
    filtered: pick(filteredRows),
  };
}

/** Zay's filterAll() age rules. "any" keeps every account. */
export function ageFilter(accounts, mode) {
  switch (mode) {
    case "past_due":
      return accounts.filter((a) => (a.d30 || 0) + (a.d60 || 0) + (a.d90 || 0) + atRisk(a) > 0);
    case "d60":
      return accounts.filter((a) => (a.d90 || 0) + atRisk(a) > 0);
    case "d90":
      return accounts.filter((a) => atRisk(a) > 0);
    case "current":
      return accounts.filter((a) => (a.cu || 0) > 0);
    default:
      return accounts;
  }
}

/**
 * The type / status / name search every table offers. Empty values keep all.
 * `segment` is "commercial" or "residential".
 */
export function tableFilter(accounts, { segment = "", status = "", query = "" } = {}) {
  const q = String(query ?? "")
    .trim()
    .toLowerCase();
  return accounts.filter(
    (a) =>
      (!segment || a.segment === segment) &&
      (!status || a.status === status) &&
      (!q || a.name.toLowerCase().includes(q)),
  );
}

/** Sort by one field; strings compare by locale, numbers numerically. dir 1 asc, -1 desc. */
export function sortAccounts(accounts, key, dir = -1) {
  return [...accounts].sort((x, y) => {
    const a = x[key] ?? 0;
    const b = y[key] ?? 0;
    if (typeof a === "string" || typeof b === "string") {
      return String(a).localeCompare(String(b)) * dir;
    }
    return (a - b) * dir;
  });
}

/** Rows to accounts: newest snapshot, the customer's segment rule, the division filter. */
export function accountsFromRows(rows, rule, divisions) {
  const snap = latestSnapshot(rows);
  const accounts = snap.rows.map((r) => toAccount(r, rule));
  return { snapshotDate: snap.date, accounts: filterByDivision(accounts, divisions) };
}
